// Inhalt der Kopfzeile.
//
// Der Kopf ist die einzige Ablesefläche des Charts: im Ruhezustand zeigt er das
// nächste Meeting, beim Fahren über den Chart den Punkt unter dem Cursor. Damit
// gibt es keine zweite Stelle, an der Zahlen erscheinen, und der Blick muss
// beim Hovern nicht zwischen zwei Bereichen wandern.
//
// Drei Dinge sollen ohne Nachdenken ablesbar sein:
//
//   1. die Rechnung — Anker plus Eingepreistes ergibt PIPR. Vorher standen die
//      drei Zahlen nebeneinander, ohne dass ihr Zusammenhang sichtbar war;
//      jetzt tragen sie Operatoren und stehen in dieser Reihenfolge.
//   2. die Entscheidung — was der Markt für das nächste Meeting als
//      wahrscheinlichstes Ergebnis sieht, in Worten, mit Betrag und Anteil.
//      Für einen Tag, dessen Meeting inzwischen gefallen ist, tritt an ihre
//      Stelle der tatsächliche Beschluss samt der Wahrscheinlichkeit, die der
//      Markt ihm damals gab — das ist die interessantere Zahl als der Modus.
//   3. die Verteilung — unverändert der Balken, ergänzt um die Modalzeile.
//
// Dazwischen steht, solange ein Schritt beschlossen und noch nicht wirksam ist,
// der beschlossene Stand. Der Anker bleibt daneben der geltende Satz — beide
// Zahlen sind gleichzeitig wahr, und genau das ist die Aussage.
//
// Reine Funktion, kein DOM-Bezug.

import {
    binLabel,
    deltaCompact,
    deltaLabel,
    localDate,
    localDateTime,
    pct,
    pctInt,
    tsDate,
    utcDateTime,
    utcDayShort,
} from "./format";
import { binColor, directionColor } from "./palette";
import { toneColor } from "./chart/layers/decisionTone";
import type { Day, Hover } from "./chart/model";
import type { Strings } from "./strings";
import type { PiprBundle } from "./types";

export interface SummaryBin {
    label: string;
    pctLabel: string;
    color: string;
    /** Anteil 0..1, steuert die Breite im Verteilungsbalken */
    share: number;
    isModal: boolean;
}

export interface SummaryStat {
    label: string;
    value: string;
    sub?: string;
    color?: string;
}

export interface Summary {
    headline: string;
    headlineCaption: string;
    /** die beiden Größen, aus denen die Headline besteht */
    anchor: SummaryStat;
    /** beschlossener, noch nicht wirksamer Stand; fehlt, wenn keiner offen ist */
    decided?: SummaryStat;
    priced: SummaryStat;
    /** erwartete oder — wenn sie gefallen ist — beschlossene Entscheidung */
    decision: SummaryStat;
    meeting: SummaryStat;
    binsLabel: string;
    /** "Modus −25 bp · 68 %" */
    binsNote: string;
    bins: SummaryBin[];
    /** Zusatzzeile mit UTC-Zeit; im Ruhezustand der Beschlusstag oder leer */
    note: string;
    /** false, sobald ein Punkt im Chart gelesen wird */
    isLive: boolean;
}

function countdown(fromSec: number, toSec: number, s: Strings): string {
    const days = Math.round((toSec - fromSec) / 86400);
    if (days <= 0) return s.dayToday;
    if (days === 1) return s.dayTomorrow;
    return s.inDays.replace("{n}", String(days));
}

function bins(p: number[], deltas: number[], lang: string): SummaryBin[] {
    const modal = modalIndex(p);
    return p.map((q, j) => ({
        label: binLabel(deltas[j], j, deltas.length, lang),
        pctLabel: pctInt(q, lang),
        color: binColor(deltas[j]),
        share: q / 1000,
        isModal: j === modal,
    }));
}

function modalIndex(p: number[]): number {
    let best = 0;
    for (let j = 1; j < p.length; j++) if (p[j] > p[best]) best = j;
    return best;
}

/** "Senkung (−25 bp)" — bei Stillstand ohne Betrag, der wäre immer 0. */
function decisionValue(delta: number, s: Strings, lang: string): string {
    if (delta === 0) return s.hold;
    const word = delta > 0 ? s.hike : s.cut;
    return word + " (" + deltaLabel(delta, lang) + ")";
}

/** Wahrscheinlichstes Ergebnis einer Verteilung. */
function likely(p: number[], deltas: number[], s: Strings, lang: string): SummaryStat {
    const j = modalIndex(p);
    return {
        label: s.decisionLikely,
        value: decisionValue(deltas[j], s, lang),
        sub: pctInt(p[j], lang) + " " + s.probability,
        color: toneColor(deltas[j]),
    };
}

/**
 * Gefallener Beschluss, mit der Wahrscheinlichkeit, die der Markt ihm an diesem
 * Tag gab — die aussagekräftigere Zahl als der Modus, weil man daran sieht, ob
 * der Markt richtig lag. Steht der Beschluss nicht im Delta-Raster, entfällt der
 * Anteil: lieber keine Zahl als eine erfundene.
 */
function settled(
    decidedBp: number,
    p: number[],
    deltas: number[],
    s: Strings,
    lang: string,
): SummaryStat {
    const j = deltas.indexOf(decidedBp);
    return {
        label: s.decisionMade,
        value: decisionValue(decidedBp, s, lang),
        sub: j >= 0 ? pctInt(p[j], lang) + " " + s.pricedThen : undefined,
        color: toneColor(decidedBp),
    };
}

/**
 * Der beschlossene Stand. `sub` nennt den Wirksamkeitstag, wenn er bekannt ist —
 * in der Historie ist er es nicht, dort steht nur, dass der Schritt noch nicht
 * greift.
 */
function decidedStat(
    anchorBp: number,
    pendingBp: number,
    effectiveTs: number | null,
    s: Strings,
    lang: string,
): SummaryStat {
    return {
        label: s.decided,
        value: pct(anchorBp + pendingBp, lang, 2),
        sub: effectiveTs
            ? s.effectiveFrom.replace("{d}", utcDayShort(tsDate(effectiveTs), lang))
            : s.notYetEffective,
        color: directionColor(pendingBp),
    };
}

function binsNote(p: number[], deltas: number[], s: Strings, lang: string): string {
    const j = modalIndex(p);
    return s.modal + " " + binLabel(deltas[j], j, deltas.length, lang) + "  ·  " + pctInt(p[j], lang);
}

export function buildSummary(
    data: PiprBundle,
    days: Day[],
    hover: Hover,
    s: Strings,
    lang: string,
): Summary | null {
    const deltas = data.history.deltas;

    // --- gehoverter Tag in der Historie ---------------------------------------
    if (hover?.kind === "hist") {
        const d = days[hover.i];
        if (d) {
            const meeting = data.meetings[d.meetingIdx];
            const when = meeting ? tsDate(meeting.decisionTs) : d.date;
            const obsSec = Math.floor(d.date.getTime() / 1000);
            const change = d.implied - d.anchor - d.pending;
            const decided = meeting ? meeting.decidedBp : null;

            return {
                headline: pct(d.implied, lang, 3),
                headlineCaption: s.impliedFor + " " + localDate(when, lang),
                anchor: {
                    label: d.pending !== 0 ? s.inForce : s.anchorPlain,
                    value: pct(d.anchor, lang, 2),
                },
                decided:
                    d.pending !== 0
                        ? decidedStat(d.anchor, d.pending, null, s, lang)
                        : undefined,
                priced: {
                    label: s.pricedIn,
                    value: deltaCompact(change, lang),
                    color: directionColor(change),
                },
                decision:
                    decided !== null
                        ? settled(decided, d.p, deltas, s, lang)
                        : likely(d.p, deltas, s, lang),
                meeting: {
                    label: decided !== null ? s.meetingOn : s.pricedMeeting,
                    value: localDate(when, lang),
                    sub: meeting && decided === null
                        ? countdown(obsSec, meeting.decisionTs, s)
                        : undefined,
                },
                binsLabel: s.distribution + " " + localDate(when, lang),
                binsNote: binsNote(d.p, deltas, s, lang),
                bins: bins(d.p, deltas, lang),
                note:
                    localDateTime(d.date, lang) +
                    "  ·  " +
                    s.close +
                    " " +
                    utcDateTime(d.date, lang) +
                    " UTC",
                isLive: false,
            };
        }
    }

    // --- gehovertes künftiges Meeting -----------------------------------------
    if (hover?.kind === "fwd") {
        const m = data.forward.meetings[hover.i];
        const when = tsDate(m.decisionTs);
        const change = m.rateBp - m.baseBp;

        return {
            headline: pct(m.rateBp, lang, 2),
            headlineCaption: s.impliedFor + " " + localDate(when, lang),
            anchor: { label: s.basisBefore, value: pct(m.baseBp, lang, 2) },
            priced: {
                label: s.pricedIn,
                value: deltaCompact(change, lang),
                color: directionColor(change),
            },
            decision: likely(m.p, deltas, s, lang),
            meeting: {
                label: s.pricedMeeting,
                value: localDate(when, lang),
                sub: countdown(data.forward.asofTs, m.decisionTs, s),
            },
            binsLabel: s.distribution + " " + localDate(when, lang),
            binsNote: binsNote(m.p, deltas, s, lang),
            bins: bins(m.p, deltas, lang),
            note: s.decision + " " + utcDateTime(when, lang) + " UTC",
            isLive: false,
        };
    }

    // --- Ruhezustand: nächstes Meeting ----------------------------------------
    const front = data.forward.meetings[0];
    if (!front) return null;

    const fwd = data.forward;
    const when = tsDate(front.decisionTs);
    const change = front.rateBp - front.baseBp;
    const pendingOpen = fwd.pendingBp !== 0;

    return {
        headline: pct(front.rateBp, lang, 2),
        headlineCaption: s.impliedFor + " " + localDate(when, lang),
        anchor: {
            label: pendingOpen ? s.inForce : s.anchorToday,
            value: pct(fwd.anchorBp, lang, 2),
        },
        decided: pendingOpen
            ? decidedStat(fwd.anchorBp, fwd.pendingBp, fwd.pendingEffectiveTs, s, lang)
            : undefined,
        priced: {
            label: s.pricedIn,
            value: deltaCompact(change, lang),
            color: directionColor(change),
        },
        decision: likely(front.p, deltas, s, lang),
        meeting: {
            label: s.nextMeeting,
            value: localDate(when, lang),
            sub: countdown(fwd.asofTs, front.decisionTs, s),
        },
        binsLabel: s.distribution + " " + localDate(when, lang),
        binsNote: binsNote(front.p, deltas, s, lang),
        bins: bins(front.p, deltas, lang),
        note:
            pendingOpen && fwd.pendingAnnouncedTs
                ? s.announcedOn.replace("{d}", utcDayShort(tsDate(fwd.pendingAnnouncedTs), lang))
                : "",
        isLive: true,
    };
}